import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ButtonHTMLAttributes, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from "react";
import { Icon, IconButton, type IconName } from "@bedrock/ui";

interface Position {
  x: number;
  y: number;
}

interface Size {
  width: number;
  height: number;
}

export const CONTEXT_MENU_EXIT_DURATION_MS = 120;
export type CloseContextMenu = (afterClose?: () => void) => void;
const MOBILE_MENU_MEDIA_QUERY = "(max-width: 600px)";

function useMobileMenu(): boolean {
  const [mobile, setMobile] = useState(() => typeof window !== "undefined" && window.matchMedia(MOBILE_MENU_MEDIA_QUERY).matches);

  useEffect(() => {
    const media = window.matchMedia(MOBILE_MENU_MEDIA_QUERY);
    const update = (event: MediaQueryListEvent) => setMobile(event.matches);
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  return mobile;
}

export function ContextMenuHeader({ title, eyebrow, closeLabel = "Close menu", onClose }: {
  title?: string;
  eyebrow?: string;
  closeLabel?: string;
  onClose: () => void;
}) {
  return <header className="context-menu-header">
    <div className="context-menu-header__copy">
      {eyebrow && <span>{eyebrow}</span>}
      {title && <strong>{title}</strong>}
    </div>
    <IconButton label={closeLabel} icon="close" onClick={onClose}/>
  </header>;
}

export function ContextMenuItem({ icon, label, description, shortcut, accent = false, tone = "default", menuItem = true, className = "", ...props }: {
  icon: IconName;
  label: string;
  description?: string;
  shortcut?: string;
  accent?: boolean;
  tone?: "default" | "danger";
  menuItem?: boolean;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children">) {
  const mobile = useMobileMenu();
  return <button className={`context-menu-item context-menu-item--${tone} ${className}`} role={menuItem ? "menuitem" : undefined} {...props} aria-keyshortcuts={mobile ? undefined : props["aria-keyshortcuts"]}>
    <span className={`context-menu-item__icon ${accent ? "is-accent" : ""}`}><Icon name={icon}/></span>
    <span className="context-menu-item__copy"><strong>{label}</strong>{description && <small>{description}</small>}</span>
    {!mobile && shortcut && <kbd>{shortcut}</kbd>}
  </button>;
}

export function ContextMenuDivider() {
  return <div className="context-menu-divider" role="separator"/>;
}

export function fitContextMenuToViewport(anchor: Position, menu: Size, viewport: Size, margin = 12): Position {
  return {
    x: Math.min(Math.max(margin, anchor.x), Math.max(margin, viewport.width - menu.width - margin)),
    y: Math.min(Math.max(margin, anchor.y), Math.max(margin, viewport.height - menu.height - margin))
  };
}

export function ContextMenu({ anchor, label, className = "", children, onClose }: {
  anchor: Position;
  label: string;
  className?: string;
  children: ReactNode | ((close: CloseContextMenu) => ReactNode);
  onClose: () => void;
}) {
  const menuRef = useRef<HTMLElement>(null);
  const closeTimer = useRef<number | null>(null);
  const [position, setPosition] = useState({ ...anchor, ready: false });
  const [closing, setClosing] = useState(false);

  const requestClose = useCallback<CloseContextMenu>((afterClose) => {
    if (closeTimer.current !== null) return;
    const closeCallback = typeof afterClose === "function" ? afterClose : undefined;
    setClosing(true);
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    closeTimer.current = window.setTimeout(() => {
      closeTimer.current = null;
      onClose();
      closeCallback?.();
    }, reducedMotion ? 0 : CONTEXT_MENU_EXIT_DURATION_MS);
  }, [onClose]);

  useEffect(() => () => {
    if (closeTimer.current !== null) window.clearTimeout(closeTimer.current);
  }, []);

  useLayoutEffect(() => {
    const menu = menuRef.current;
    if (!menu) return;

    const placeMenu = () => {
      const bounds = menu.getBoundingClientRect();
      const next = fitContextMenuToViewport(anchor, bounds, { width: window.innerWidth, height: window.innerHeight });
      setPosition((current) => current.ready && current.x === next.x && current.y === next.y ? current : { ...next, ready: true });
    };

    placeMenu();
    window.addEventListener("resize", placeMenu);
    if (typeof ResizeObserver === "undefined") return () => window.removeEventListener("resize", placeMenu);
    const observer = new ResizeObserver(placeMenu);
    observer.observe(menu);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", placeMenu);
    };
  }, [anchor.x, anchor.y]);

  useEffect(() => {
    const closeOnPointerDown = (event: PointerEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) requestClose();
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.isComposing || event.defaultPrevented) return;
      event.preventDefault();
      event.stopPropagation();
      requestClose();
    };
    window.addEventListener("pointerdown", closeOnPointerDown, { capture: true });
    window.addEventListener("keydown", closeOnEscape, { capture: true });
    return () => {
      window.removeEventListener("pointerdown", closeOnPointerDown, { capture: true });
      window.removeEventListener("keydown", closeOnEscape, { capture: true });
    };
  }, [requestClose]);

  useEffect(() => {
    if (position.ready) menuRef.current?.querySelector<HTMLElement>("[role='menuitem']:not([disabled]), [role='menuitemradio']:not([disabled])")?.focus();
  }, [position.ready]);

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      requestClose();
      return;
    }
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    const items = [...(menuRef.current?.querySelectorAll<HTMLElement>("[role='menuitem']:not([disabled]), [role='menuitemradio']:not([disabled])") ?? [])];
    if (!items.length) return;
    event.preventDefault();
    const currentIndex = items.findIndex((item) => item === document.activeElement);
    const step = event.key === "ArrowDown" ? 1 : -1;
    items[(currentIndex + step + items.length) % items.length]?.focus();
  };

  return <section ref={menuRef} className={`br-floating-panel canvas-context-menu ${closing ? "is-closing" : ""} ${className}`} role="menu" aria-label={label}
    style={{ left: position.x, top: position.y, visibility: position.ready ? "visible" : "hidden" }}
    onKeyDown={handleKeyDown}
    onContextMenu={(event) => event.preventDefault()}>
    {typeof children === "function" ? children(requestClose) : children}
  </section>;
}
